// BUILD-046 Owner-requested layout revision (visual edits ledger, Layout-2): the bodies of the toolbar's ONE "Filter" popover.
//
// Only controls that already existed move here, with the same choices and the same defaults (nothing is added, nothing is renamed except where a group needs a
// legend):
//   My Tasks : the quick filters of sortModel.ts (All, Urgent, High and urgent, Due today, Overdue, No due date), default All.
//   Team / Company : the department (a client-side display filter of the loaded rows), default All departments.
// BUILD-046 J1a adds one more choice to each, "Customer / Job" (All jobs, No job, or one job), drawn only when the loaded tasks carry a job; Shared Tasks gets a Filter button
// with that single choice, and only when a loaded task has a job. Since cycle C (Edit 3) that choice is a SEARCHABLE picker (JobFilterPicker.tsx: quick choices All jobs and
// No job, a search box, at most eight matches after the person types), not a native drop-down of every job; an active job filter also shows as a removable chip above the table.
// Section B (Owner direction of 2026-10-21): the "Assigned to" picker moved to the toolbar (AssigneeToolbarButton.tsx) and Show requester to the Display settings menu (DisplayMenu.tsx). Company / Team's Filter panel holds
// Department (the departments on the LOADED rows, activeFilters.distinctDepartments) and Customer / Job; Assigned by Me has no Filter panel.
// The popover itself (open / close / Escape / focus) is DropdownMenu.PopoverButton;
// this file only draws the controls inside it. The other controls are native radios and selects, so they are keyboard operable and announced with their label.
import type { QuickFilter } from './sortModel';
import { JobFilterPicker } from './JobFilterPicker';
// BUILD-046 (Customer association): the Customer picker is drawn BESIDE the Job picker, never instead of it. When both are drawn the Job picker's heading says "Job" (the page gives it that label).
import { CustomerFilterPicker } from './CustomerFilterPicker';

/** The My Tasks quick filters: the legacy ones (sortModel.ts QuickFilter, byte-identical) plus Follow-up due (BUILD-046 G4), a date condition the legacy page never had. */
export type MineQuickFilter = QuickFilter | 'follow-up-due';
import type { JobPickerControl } from './JobFilterPicker';
import type { CustomerPickerControl } from './CustomerFilterPicker';

/**
 * The pickers a Filter popover can draw: the Customer picker first, then the Job picker, each only when the page offers it. One alone is drawn exactly as the Job picker always was (no wrapper);
 * two are stacked with the panels' usual gap.
 */
function AssociationPickers({ jobPicker, customerPicker }: { jobPicker: JobPickerControl | null; customerPicker: CustomerPickerControl | null }) {
  const customer = customerPicker !== null ? <CustomerFilterPicker {...customerPicker} /> : null;
  const job = jobPicker !== null ? <JobFilterPicker {...jobPicker} /> : null;
  return customer !== null && job !== null ? (
    <div className="space-y-3">
      {customer}
      {job}
    </div>
  ) : (
    <>
      {customer}
      {job}
    </>
  );
}

/** The Filter popover of Shared Tasks, which has no other filter: only the Customer / Job and Customer pickers (the page draws the Filter button only when it has a job or a customer to offer). */
export function SharedFilterPanel({ jobPicker = null, customerPicker = null }: { jobPicker?: JobPickerControl | null; customerPicker?: CustomerPickerControl | null }) {
  return <AssociationPickers jobPicker={jobPicker} customerPicker={customerPicker} />;
}

/** The quick filters, in their existing order, with their existing words. */
export const QUICK_FILTER_OPTIONS: readonly { value: MineQuickFilter; label: string }[] = Object.freeze([
  { value: 'all' as MineQuickFilter, label: 'All' },
  { value: 'urgent' as MineQuickFilter, label: 'Urgent' },
  { value: 'high' as MineQuickFilter, label: 'High and urgent' },
  { value: 'due-today' as MineQuickFilter, label: 'Due today' },
  { value: 'overdue' as MineQuickFilter, label: 'Overdue' },
  { value: 'follow-up-due' as MineQuickFilter, label: 'Follow-up due' },
  { value: 'no-due' as MineQuickFilter, label: 'No due date' },
]);

export interface MineFilterPanelProps {
  value: MineQuickFilter;
  onChange: (next: MineQuickFilter) => void;
  /** The searchable Customer / Job picker, when the loaded tasks carry a job (BUILD-046 J1a, cycle C). */
  jobPicker?: JobPickerControl | null;
  /** The searchable Customer picker, when the loaded tasks carry a customer (BUILD-046 Customer association). */
  customerPicker?: CustomerPickerControl | null;
}

export function MineFilterPanel({ value, onChange, jobPicker = null, customerPicker = null }: MineFilterPanelProps) {
  return (
    <div className="space-y-3">
      <fieldset>
        <legend className="mb-1.5 text-xs font-semibold text-gray-700">Filter my tasks</legend>
        <div className="space-y-1">
          {QUICK_FILTER_OPTIONS.map((option) => (
            <label key={option.value} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm text-gray-800 hover:bg-gray-50">
              <input
                type="radio"
                name="my-tasks-quick-filter"
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                className="h-4 w-4 accent-[#1f6fa5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <AssociationPickers jobPicker={jobPicker} customerPicker={customerPicker} />
    </div>
  );
}

export interface TeamFilterPanelProps {
  department: string;
  onDepartment: (next: string) => void;
  /** The searchable Customer / Job picker, when the loaded tasks carry a job (BUILD-046 J1a, cycle C). */
  jobPicker?: JobPickerControl | null;
  /** The searchable Customer picker, when the loaded tasks carry a customer (BUILD-046 Customer association). */
  customerPicker?: CustomerPickerControl | null;
  /** The departments on the loaded rows: a choice always names rows that carry it. */
  departments: readonly string[];
}

export function TeamFilterPanel({ department, onDepartment, jobPicker = null, customerPicker = null, departments }: TeamFilterPanelProps) {
  return (
    <div className="space-y-3">
      <div>
        <label htmlFor="oversight-department" className="mb-1 block text-xs font-semibold text-gray-700">
          Department
        </label>
        <select
          id="oversight-department"
          value={department}
          onChange={(event) => onDepartment(event.target.value)}
          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
        >
          <option value="">Any department</option>
          {departments.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>
      <AssociationPickers jobPicker={jobPicker} customerPicker={customerPicker} />
    </div>
  );
}
